/**
 * Spinning one motor on the bench.
 *
 * The only control in this app that makes a propeller turn next to somebody's
 * hands, so it is the only one that asks twice. The first click arms the
 * confirmation; the second sends it. The server refuses the request outright
 * without `confirm`, and refuses it again if the aircraft is not on the ground —
 * this component's job is to make sure a human meant it, not to be the check.
 *
 * The bounds shown here (30%, 10 s, 12 motors) mirror
 * `api/app/domain/maintenance.py`. They are duplicated rather than fetched
 * because they are input constraints, and a slider that lets an operator pick a
 * number the server will reject is worse than one that cannot. The server is
 * still the authority: it validates independently and answers 422.
 */

import { useState, type ReactNode } from 'react';

import { droneCommands } from '@/api/drones';
import { isSurfaceVehicle } from '@/features/fleet/vehicleKind';
import { useCanControl } from '@/stores/authStore';
import { useDrone } from '@/stores/droneStore';
import type { CommandResult, DroneId } from '@/types/domain';

import { DriveTestPanel } from './DriveTestPanel';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';
const MUTED = 'rgba(244,244,245,0.5)';

/** Mirrors MAX_MOTOR_INDEX / MAX_THROTTLE_PERCENT / MAX_DURATION_S. */
const MAX_MOTOR = 12;
const MAX_THROTTLE = 30;
const MAX_DURATION = 10;

export function MotorTestPanel({ droneId }: { droneId: DroneId }): ReactNode {
  const canCommand = useCanControl(droneId);
  const drone = useDrone(droneId);
  const [motor, setMotor] = useState(1);
  const [throttle, setThrottle] = useState(10);
  const [duration, setDuration] = useState(2);
  const [arming, setArming] = useState(false);
  const [result, setResult] = useState<CommandResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const armed = drone?.scalars.armed === true;

  // ArduRover reads this command's motor number as a role, so motor 2 would be
  // the rudder. Rovers and boats get their steering and throttle by name.
  if (isSurfaceVehicle(drone?.scalars.mavType)) return <DriveTestPanel droneId={droneId} />;

  async function send() {
    setBusy(true);
    setError(null);
    setResult(null);
    const response = await droneCommands.testMotor(droneId, motor, throttle, duration);
    setBusy(false);
    setArming(false);
    if (response.ok) setResult(response.data);
    else setError(response.error.message);
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        className="rounded-[12px] border px-3 py-2.5 text-[11px]"
        style={{
          background: 'color-mix(in oklch, oklch(0.72 0.19 28) 9%, transparent)',
          borderColor: 'color-mix(in oklch, oklch(0.72 0.19 28) 38%, transparent)',
        }}
      >
        <span className="label-mono text-[9px]" style={{ color: DANGER, letterSpacing: '0.14em' }}>
          PROPELLERS OFF
        </span>
        <p className="mt-1" style={{ color: 'rgba(244,244,245,0.85)' }}>
          This turns a motor. Remove the propellers and keep hands clear. The aircraft must be on
          the ground; the server refuses this in flight.
        </p>
      </div>

      {!canCommand && (
        <p className="text-[11px]" style={{ color: MUTED }}>
          Read-only: a motor test needs operator role and control of this aircraft.
        </p>
      )}
      {armed && (
        <p className="text-[11px]" style={{ color: WARN }}>
          This aircraft is armed. Disarm it before testing a motor.
        </p>
      )}

      <div className="flex flex-col gap-2.5">
        <Field label={`MOTOR ${motor}`} hint={`1 – ${MAX_MOTOR}`}>
          <input
            type="range"
            min={1}
            max={MAX_MOTOR}
            step={1}
            value={motor}
            aria-label="Motor number"
            onChange={(e) => {
              setMotor(Number(e.target.value));
              setArming(false);
            }}
            className="w-full"
          />
        </Field>
        <Field label={`THROTTLE ${throttle}%`} hint={`max ${MAX_THROTTLE}%`}>
          <input
            type="range"
            min={1}
            max={MAX_THROTTLE}
            step={1}
            value={throttle}
            aria-label="Throttle percent"
            onChange={(e) => {
              setThrottle(Number(e.target.value));
              setArming(false);
            }}
            className="w-full"
          />
        </Field>
        <Field label={`DURATION ${duration}s`} hint={`max ${MAX_DURATION}s`}>
          <input
            type="range"
            min={1}
            max={MAX_DURATION}
            step={1}
            value={duration}
            aria-label="Duration seconds"
            onChange={(e) => {
              setDuration(Number(e.target.value));
              setArming(false);
            }}
            className="w-full"
          />
        </Field>
      </div>

      {!arming ? (
        <button
          type="button"
          disabled={!canCommand || busy || armed}
          onClick={() => setArming(true)}
          className="w-fit rounded-[10px] border px-4 py-2 label-mono text-[11px] disabled:opacity-40"
          style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(244,244,245,0.85)' }}
        >
          TEST MOTOR {motor}
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => void send()}
            className="rounded-[10px] border px-4 py-2 label-mono text-[11px] disabled:opacity-40"
            style={{
              borderColor: 'color-mix(in oklch, oklch(0.72 0.19 28) 55%, transparent)',
              background: 'color-mix(in oklch, oklch(0.72 0.19 28) 18%, transparent)',
              color: DANGER,
            }}
          >
            {busy ? 'SENDING…' : `CONFIRM — SPIN MOTOR ${motor} AT ${throttle}%`}
          </button>
          <button
            type="button"
            onClick={() => setArming(false)}
            className="rounded-[10px] border px-3 py-2 label-mono text-[10px]"
            style={{ borderColor: 'rgba(255,255,255,0.18)', color: MUTED }}
          >
            BACK
          </button>
        </div>
      )}

      {result && (
        <p className="text-[11px]" style={{ color: result.ok ? OK : DANGER }}>
          {result.message || (result.ok ? 'Accepted.' : 'Refused.')}
        </p>
      )}
      {error && (
        <p className="text-[11px]" style={{ color: DANGER }}>
          {error}
        </p>
      )}
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint: string;
  children: ReactNode;
}): ReactNode {
  return (
    <label className="flex flex-col gap-1">
      <span className="flex items-baseline gap-2">
        <span className="label-mono text-[10px]" style={{ color: 'rgba(244,244,245,0.8)' }}>
          {label}
        </span>
        <span className="label-mono text-[9px]" style={{ color: MUTED }}>
          {hint}
        </span>
      </span>
      {children}
    </label>
  );
}
